/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { iavActions } from "src/context/iav-slice";
import { RootState } from "src/context/store";
import { toast } from "src/hooks/useToast";
import { getOpsQueue } from "src/services/iav-api";
import { downloadInvoice } from "src/services/invoice-api";

export const IAV_CATEGORY = {
  STAMP_SIGN_REVIEW: "STAMP_SIGN_REVIEW",
  DISPUTE: "DISPUTE",
} as const;

export const IAV_CATEGORY_LABELS: Record<string, string> = {
  [IAV_CATEGORY.STAMP_SIGN_REVIEW]: "Stamp & Sign Review",
  [IAV_CATEGORY.DISPUTE]: "Disputes",
};

// Title of the Invoices page tab. Invoice-homepage resolves /transactions/invoice/<slug> to a tab
// by its title slug, so the queue URL is derived from it.
export const IAV_TAB_TITLE = "POD Acknowledgement";
export const IAV_TAB_SLUG = IAV_TAB_TITLE.toLowerCase().replace(/\s+/g, "-"); // "pod-acknowledgement"
export const IAV_QUEUE_URL = `/transactions/invoice/${IAV_TAB_SLUG}`;
export const INVOICES_URL = "/transactions/invoice/0";

export const iavDetailUrl = (category: string, invoiceId: number | string) =>
  `${IAV_QUEUE_URL}/${category === IAV_CATEGORY.DISPUTE ? "dispute" : "stamp-sign"}/${invoiceId}`;

// Mirrors IAV_ACK_STATUS in backend-oms-svc iav.constants.ts
export const IAV_ACK_STATUS_LABELS: Record<number, string> = {
  1: "Pending Acknowledgement",
  2: "Pending Ops Review",
  3: "Acknowledged",
  4: "Disputed",
  5: "Disputed - Reminder 1",
  6: "Disputed - Reminder 2",
  7: "Disputed - Reminder 3 (Exhausted)",
  8: "Rejected - Resubmit",
  9: "Overridden by Ops",
  10: "Exempted",
};

export const iavStatusLabel = (statusId: number | null | undefined) =>
  statusId ? IAV_ACK_STATUS_LABELS[statusId] ?? String(statusId) : "—";

// Status filter options for each queue category
export const IAV_STATUS_OPTIONS: Record<string, { key: number; value: string }[]> = {
  [IAV_CATEGORY.STAMP_SIGN_REVIEW]: [2].map((key) => ({ key, value: IAV_ACK_STATUS_LABELS[key] })),
  [IAV_CATEGORY.DISPUTE]: [4, 5, 6, 7].map((key) => ({ key, value: IAV_ACK_STATUS_LABELS[key] })),
};

export const IAV_DATE_TYPES = [
  { key: "invoice_date", value: "Invoice Date" },
  { key: "approved_date", value: "Invoice Approved Date" },
  { key: "assigned_at", value: "Assigned At" },
  { key: "dispute_started", value: "Dispute Started" },
];

export const IAV_SEARCH_OPTIONS = [
  { value: "invoice_number", label: "Invoice No." },
  { value: "counter_code", label: "Counter Code" },
  { value: "company_stockist_code", label: "Stockist Code (CMP)" },
];

export const formatIavDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString("en-IN") : "—";

// Days since the item entered the queue: assignment for stamp-sign, dispute start for disputes
export const iavQueueAgeDays = (invoice: any, category: string) => {
  const since = category === IAV_CATEGORY.DISPUTE ? invoice?.dispute_email_sent_at : invoice?.ack_assigned_at;
  return since ? Math.floor((Date.now() - new Date(since).getTime()) / 86400000) : null;
};

// Opens the invoice in the Invoice Approval screen (read-only once approved) in a new tab — same as
// the order screen's invoice link. The new tab can't read router state, so company_code goes via
// localStorage, which that screen already reads.
export const openInvoiceDetails = (invoice: any) => {
  const sia = invoice?.stockistInvoiceAttachment;
  if (!sia?.stockist_invoice_attachment_id) {
    toast({ description: "Invoice details not found", variant: "destructive" });
    return;
  }
  window.localStorage.setItem("company_code", String(sia.company_code ?? ""));
  window.open(`/transactions/invoice/${sia.stockist_invoice_attachment_id}/1`, "_blank");
};

// Downloads an S3 file through the get-file API (the bucket stays in the backend env), same as the
// invoice download on the Entry / Approval cards.
export const downloadIavFile = (filePath: string | null | undefined, companyCode: any, label: string) => {
  if (!filePath) {
    toast({ description: `${label} not found`, variant: "destructive" });
    return;
  }
  toast({ title: "Downloading...", description: `Downloading ${label.toLowerCase()}`, variant: "default" });
  downloadInvoice(filePath, companyCode)
    .then((res: any) => {
      if (res?.data?.success && res?.data?.data) {
        window.open(res.data.data, "_blank");
      } else {
        toast({ description: `Failed to download the ${label.toLowerCase()}`, variant: "destructive" });
      }
    })
    .catch(() => toast({ description: `Failed to download the ${label.toLowerCase()}`, variant: "destructive" }));
};

// The detail screens normally get their invoice from the queue row that was clicked. On a direct
// link or a page refresh there is none, so load that one row from the ops queue.
export const useIavInvoice = (invoiceId: string | undefined, category: string) => {
  const dispatch = useDispatch();
  const selectedInvoice = useSelector((state: RootState) => state.iav.selectedInvoice);
  const isLoaded = !!invoiceId && Number(selectedInvoice?.invoice_id) === Number(invoiceId);

  useEffect(() => {
    if (!invoiceId || isLoaded) return;
    getOpsQueue({ category, invoice_id: invoiceId, page: 1, limit: 1 })
      .then((res) => dispatch(iavActions.setSelectedInvoice(res.data?.data?.items?.[0] ?? null)))
      .catch((err) => console.error("IAV invoice fetch error:", err));
  }, [invoiceId, isLoaded, category, dispatch]);

  return isLoaded ? selectedInvoice : null;
};
